import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  finalizeUploadedPhoto,
  getPhotoGroups,
  requestPhotoUploadUrl,
  uploadFileToSignedUrl,
} from '../api/photo-gallery-api';
import type { PhotoGalleryFilters, PhotoGalleryPageParams, PhotoSourceType } from '../types';

export const photoGalleryKeys = {
  all: ['photo-gallery'] as const,
  lists: (projectId: number | undefined) => [...photoGalleryKeys.all, 'list', projectId] as const,
  list: (projectId: number | undefined, filters: PhotoGalleryFilters) =>
    [...photoGalleryKeys.lists(projectId), filters] as const,
  infiniteList: (
    projectId: number | undefined,
    filters: PhotoGalleryFilters,
    pageParams: Pick<PhotoGalleryPageParams, 'limit'>,
  ) => [...photoGalleryKeys.list(projectId, filters), 'infinite', pageParams] as const,
};

export const usePhotoGroups = (projectId: number | undefined, filters: PhotoGalleryFilters = {}) =>
  useQuery({
    queryKey: photoGalleryKeys.list(projectId!, filters),
    queryFn: () => getPhotoGroups(projectId!, filters),
    enabled: !!projectId,
    staleTime: 30_000,
  });

export const useInfinitePhotoGroups = (
  projectId: number | undefined,
  filters: PhotoGalleryFilters = {},
  pageSize = 24,
) =>
  useInfiniteQuery({
    queryKey: photoGalleryKeys.infiniteList(projectId, filters, { limit: pageSize }),
    queryFn: ({ pageParam }) =>
      getPhotoGroups(projectId!, filters, {
        limit: pageSize,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.next_offset ?? undefined,
    enabled: !!projectId,
    staleTime: 30_000,
  });

export const useUploadPhotos = (projectId: number | undefined, sourceType: PhotoSourceType = 'gallery') => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      files,
      onFileProgress,
    }: {
      files: File[];
      onFileProgress?: (index: number, progressPct: number) => void;
    }) => {
      if (!projectId) {
        throw new Error('A project id is required before uploading.');
      }

      const uploaded = [];
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const signed = await requestPhotoUploadUrl({
          project_id: projectId,
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
          source_type: sourceType,
        });

        await uploadFileToSignedUrl(file, signed.signed_upload_url, (pct) => onFileProgress?.(i, pct));

        const photo = await finalizeUploadedPhoto({
          project_id: projectId,
          photo_uuid: signed.photo_uuid,
          gcs_object_path: signed.gcs_object_path,
          original_file_name: file.name,
          mime_type: file.type || '',
          file_size: file.size,
          source_type: sourceType,
          captured_at: null,
        });
        uploaded.push(photo);
      }
      return uploaded;
    },
    onSuccess: () => {
      if (!projectId) return;
      queryClient.invalidateQueries({ queryKey: photoGalleryKeys.lists(projectId) });
    },
  });
};
